import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Tooltip,
  Stack,
  TablePagination
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import dayOfYear from "dayjs/plugin/dayOfYear";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";

dayjs.extend(dayOfYear);

export default function TimeSheetPage() {
  const API_URL = window.__ENV__?.API_URL  || "https://m2oi2mvv2e.execute-api.ap-south-1.amazonaws.com/Prod/api";
  const [history, setHistory] = useState([]);
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs());
  const [comments, setComments] = useState("");
  const [leaveDate, setLeaveDate] = useState(null);
  const [leaveApplied, setLeaveApplied] = useState({});
  const [holidays, setHolidays] = useState({});
  const [dailyQuote, setDailyQuote] = useState("");
  const [emojiReactions, setEmojiReactions] = useState({ "👍": 0, "❤️": 0, "😍": 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  const userId = localStorage.getItem("userId");
  const userName = localStorage.getItem("userName") || "Team";

  const hour = dayjs().hour();
  let greeting = "Hello";
  if (hour < 12) greeting = "Good Morning";
  else if (hour < 17) greeting = "Good Afternoon";
  else greeting = "Good Evening";

  const quoteColors = ["#e3f2fd", "#fce4ec", "#f3e5f5", "#e8f5e9", "#fff3e0"];
  const quoteBgColor = quoteColors[dayjs().dayOfYear() % quoteColors.length];

  const handleEmojiClick = (emoji) => {
    setEmojiReactions((prev) => ({ ...prev, [emoji]: prev[emoji] + 1 }));
  };

  useEffect(() => {
    fetch("/holidays.json")
      .then((res) => res.json())
      .then((data) => {
        const map = {};
        data.forEach((holiday) => {
          map[holiday.date] = holiday.name;
        });
        setHolidays(map);
      });

    fetch("/quotes.json")
      .then((res) => res.json())
      .then((quotes) => {
        const today = dayjs().dayOfYear();
        const quoteIndex = today % quotes.length;
        setDailyQuote(quotes[quoteIndex]);
      });
  }, []);

  const fetchData = useCallback(() => {
    if (!userId) {
      alert("User not logged in. Redirecting to login.");
      navigate("/login");
      return;
    }
    axios
      .get(`${API_URL}/timesheets/report`, {
        params: {
          employee_id: userId,
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
        },
      })
      .then((response) => {
        setHistory(response.data);
        const leaveRecords = response.data.reduce((acc, entry) => {
          if (entry.is_leave) acc[entry.date] = true;
          return acc;
        }, {});
        setLeaveApplied(leaveRecords);
      })
      .catch((error) => {
        console.error("Error fetching time sheet history:", error);
        alert("Failed to fetch data. Please try again.");
      });
  }, [userId, startDate, endDate, API_URL, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogin = async () => {
    try {
      await axios.post(`${API_URL}/timesheets/login`, { employee_id: userId });
      alert("Login recorded!");
      fetchData();
    } catch (error) {
      console.error(error);
      alert("Error recording login.");
    }
  };

  const handleLogout = async () => {
    if (!comments.trim()) {
      alert("Please add comments before logging out.");
      return;
    }
    try {
      await axios.post(`${API_URL}/timesheets/logout`, {
        employee_id: userId,
        comments,
      });
      alert("Logout recorded! Have a good day!");
      localStorage.clear();
      navigate("/goodbye");
    } catch (error) {
      console.error(error);
      alert("Error recording logout.");
    }
  };

  const handleApplyLeave = async () => {
    if (!leaveDate) {
      alert("Please select a date for leave.");
      return;
    }
    try {
      await axios.post(`${API_URL}/timesheets/apply-leave`, {
        employee_id: userId,
        leave_date: leaveDate.format("YYYY-MM-DD"),
      });
      alert("Leave applied successfully!");
      setLeaveApplied((prev) => ({
        ...prev,
        [leaveDate.format("YYYY-MM-DD")]: true,
      }));
      setLeaveDate(null);
      fetchData();
    } catch (error) {
      console.error(error);
      alert("Error applying for leave.");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = history.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="md">
        <Typography
          variant="h4"
          align="center"
          sx={{ fontWeight: "bold", color: "#1976d2", mt: 4, mb: 2 }}
        >
          {greeting}, {userName}!
        </Typography>

        <Paper
          elevation={6}
          sx={{ p: 3, my: 4, borderRadius: 4, backgroundColor: quoteBgColor }}
        >
          <Typography variant="h6" align="center" gutterBottom>
            🌟 Daily Motivation 🌟
          </Typography>
          <Typography
            variant="body1"
            align="center"
            sx={{ fontStyle: "italic", fontSize: "1.2rem", color: "#333" }}
          >
            “{dailyQuote}”
          </Typography>
          <Box display="flex" justifyContent="center" gap={2} mt={2}>
            {["👍", "❤️", "😍"].map((emoji) => (
              <Button
                key={emoji}
                onClick={() => handleEmojiClick(emoji)}
                variant="outlined"
                sx={{ fontSize: "1.5rem", borderRadius: "999px" }}
              >
                {emoji} {emojiReactions[emoji]}
              </Button>
            ))}
          </Box>
        </Paper>

        <Box display="flex" justifyContent="center" mb={3}>
          <Button
            variant="outlined"
            color="primary"
            component={Link}
            to="/holidays"
            startIcon={<span role="img" aria-label="calendar">📅</span>}
          >
            View Holiday Calendar
          </Button>
        </Box>

        <Stack direction="row" spacing={2} justifyContent="center" mb={3}>
          <DatePicker label="Start Date" value={startDate} onChange={setStartDate} />
          <DatePicker label="End Date" value={endDate} onChange={setEndDate} />
          <Button variant="contained" onClick={fetchData}>
            Fetch Data
          </Button>
        </Stack>

        <TextField
          label="Comments"
          multiline
          rows={3}
          fullWidth
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          sx={{ mb: 3 }}
        />

        <Stack direction="row" spacing={3} justifyContent="center" mb={3}>
          <Button
            variant="contained"
            color="success"
            size="large"
            onClick={handleLogin}
            sx={{ borderRadius: 3, px: 4 }}
            disabled={leaveApplied[dayjs().format("YYYY-MM-DD")]}
          >
            Login
          </Button>
          <Button
            variant="contained"
            color="error"
            size="large"
            onClick={handleLogout}
            sx={{ borderRadius: 3, px: 4 }}
            disabled={!comments.trim() || leaveApplied[dayjs().format("YYYY-MM-DD")]}
          >
            Logout
          </Button>
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="center" mb={3}>
          <DatePicker label="Select Leave Date" value={leaveDate} onChange={setLeaveDate} />
          <Button variant="contained" color="warning" onClick={handleApplyLeave}>
            Apply Leave
          </Button>
        </Stack>

        <TableContainer component={Paper} sx={{ maxHeight: 500, borderRadius: 3 }}>
          <Table stickyHeader>
            <TableHead sx={{ backgroundColor: "#1976d2" }}>
              <TableRow>
                {["Date", "Login", "Logout", "Hours", "Comments", "Leave", "Holiday"].map((head, i) => (
                  <TableCell key={i} sx={{ fontWeight: "bold", color: "#fff" }}>
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.length > 0 ? (
                paginatedRows.map((entry, index) => (
                  <TableRow
                    key={index}
                    sx={{ backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff" }}
                  >
                    <TableCell>{entry.date}</TableCell>
                    <TableCell>{entry.login_time}</TableCell>
                    <TableCell>{entry.logout_time || "Not Available"}</TableCell>
                    <TableCell>{entry.hours_worked || "N/A"}</TableCell>
                    <TableCell>{entry.comments}</TableCell>
                    <TableCell>{entry.is_leave ? "On Leave" : "-"}</TableCell>
                    <TableCell>
                      {holidays[entry.date] ? (
                        <Tooltip title={holidays[entry.date]} arrow>
                          <Box display="flex" alignItems="center" gap={1}>
                            <span role="img" aria-label="Holiday">🏖️</span>
                            <Typography variant="body2">{holidays[entry.date]}</Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={history.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Container>
    </LocalizationProvider>
  );
}
